/**
 * 海报截图工具
 *
 * 流程：beginPosterCapture 显示全屏 loading → 海报组件 emit poster-ready
 *       → capturePosterElement 截图 → finishPosterCapture 关闭 loading
 *
 * posterState 约定字段：
 * - isCapturing: 是否显示加载遮罩（由 battalionPoster :capturing 绑定）
 * - isShow: 是否展示截图结果
 * - isPoster: 是否挂载海报组件（截图成功后置 -1 卸载）
 * - posterUrl: 截图 base64
 */
import html2canvas from 'html2canvas';
import { Toast } from 'vant';

// 防止 poster-ready 重复触发导致并发截图
const capturingPosterStates = new WeakSet();
const POSTER_LOADING_OVERLAY_ID = 'poster-capture-loading-overlay';
let posterLoadingCount = 0;

function ensurePosterLoadingStyles() {
  if (typeof document === 'undefined' || document.getElementById('poster-capture-loading-style')) {
    return;
  }
  const style = document.createElement('style');
  style.id = 'poster-capture-loading-style';
  style.textContent = `
    #${POSTER_LOADING_OVERLAY_ID} {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 10050;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.7);
    }
    #${POSTER_LOADING_OVERLAY_ID} .poster-capture-loading-box {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-width: 160px;
      padding: 26px 32px;
      background: rgba(0, 0, 0, 0.75);
      border-radius: 13px;
      color: #fff;
      font-size: 11px;
      line-height: 16px;
    }
    #${POSTER_LOADING_OVERLAY_ID} .poster-capture-loading-box p {
      margin: 10px 0 0;
    }
    @keyframes poster-capture-spin {
      to { transform: rotate(360deg); }
    }
    #${POSTER_LOADING_OVERLAY_ID} .poster-capture-loading-spinner {
      width: 29px;
      height: 29px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: poster-capture-spin 0.8s linear infinite;
    }
  `;
  document.head.appendChild(style);
}

function showPosterLoadingOverlay() {
  if (typeof document === 'undefined') {
    return;
  }
  ensurePosterLoadingStyles();
  let el = document.getElementById(POSTER_LOADING_OVERLAY_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = POSTER_LOADING_OVERLAY_ID;
    el.innerHTML = '<div class="poster-capture-loading-box"><div class="poster-capture-loading-spinner"></div><p>加载中...</p></div>';
    document.body.appendChild(el);
  }
  el.style.display = 'flex';
  posterLoadingCount += 1;
}

function hidePosterLoadingOverlay() {
  posterLoadingCount = Math.max(0, posterLoadingCount - 1);
  if (posterLoadingCount > 0) {
    return;
  }
  if (typeof document !== 'undefined') {
    const el = document.getElementById(POSTER_LOADING_OVERLAY_ID);
    if (el) {
      el.style.display = 'none';
    }
  }
  Toast.clear();
}

function waitOneImage(img, timeout) {
  return new Promise((resolve) => {
    if (!img || !img.getAttribute('src')) {
      resolve();
      return;
    }
    if (img.complete && img.naturalWidth > 0) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, timeout);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  });
}

// 兼容容器节点、组件 $el、误传的文本节点（firstChild 为空白时）
function resolvePosterElement(target) {
  if (!target) {
    return null;
  }

  let el = target.$el || target;

  if (el.nodeType === 1 && typeof el.querySelector === 'function') {
    if (el.firstElementChild && !el.querySelector('.posterBg') && !el.querySelector('img')) {
      const child = el.firstElementChild;
      if (child && typeof child.querySelector === 'function') {
        return child;
      }
    }
    return el;
  }

  if (el.parentElement && el.parentElement.firstElementChild) {
    const sibling = el.parentElement.firstElementChild;
    if (sibling && typeof sibling.querySelector === 'function') {
      return sibling;
    }
  }

  return null;
}

export function waitForPosterImages(container, options = {}) {
  const element = resolvePosterElement(container);
  if (!element) {
    return Promise.resolve();
  }

  const bgTimeout = options.bgTimeout || 15000;
  const otherTimeout = options.otherTimeout || 5000;
  const posterBg = element.querySelector('.posterBg');
  const allImages = Array.from(element.querySelectorAll('img'));
  const tasks = [];

  if (posterBg) {
    tasks.push(waitOneImage(posterBg, bgTimeout));
  }
  allImages.forEach((img) => {
    if (img !== posterBg) {
      tasks.push(waitOneImage(img, otherTimeout));
    }
  });

  return Promise.all(tasks);
}

// Toast.clear 后立即弹新 Toast 可能被吞，延迟 100ms
function showPosterToast(message) {
  Toast.clear();
  setTimeout(() => {
    Toast({
      message,
      duration: 1500,
      forbidClick: false,
    });
  }, 100);
}

function finishPosterCapture(posterState) {
  if (posterState) {
    posterState.isCapturing = false;
  }
  hidePosterLoadingOverlay();
}

/** 取消或失败时关闭 loading（与 finishPosterCapture 行为一致） */
export function cancelPosterCapture(posterState) {
  finishPosterCapture(posterState);
}

/** 打开海报弹窗时调用，重置状态并显示加载中 */
export function beginPosterCapture(posterState) {
  showPosterLoadingOverlay();
  if (posterState) {
    posterState.isCapturing = true;
    posterState.isShow = false;
    posterState.posterUrl = '';
  }
}

/** 接口赛段名可能含中文破折号（—），统一为英文连字符（-）再匹配 */
export function normalizePosterName(str) {
  if (!str) {
    return '';
  }
  return String(str).replace(/[—–－]/g, '-').trim();
}

function isRemoteUrl(src) {
  if (!src || src.indexOf('data:') === 0) {
    return false;
  }
  return src.indexOf('http') === 0 || src.indexOf('//') === 0;
}

function appendCacheBuster(url) {
  const sep = url.indexOf('?') >= 0 ? '&' : '?';
  return url + sep + '_poster=' + Date.now();
}

function drawImageToDataUrl(img) {
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (!width || !height) {
    throw new Error('empty image');
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(img, 0, 0);
  return canvas.toDataURL('image/png');
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function fetchImageAsDataUrl(url) {
  return fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-cache' })
    .then((response) => {
      if (!response.ok) {
        throw new Error('fetch failed');
      }
      return response.blob();
    })
    .then(blobToDataUrl);
}

function loadCorsImageAsDataUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        resolve(drawImageToDataUrl(img));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error('img load failed'));
    img.src = url;
  });
}

/** 远程 CDN 图转 dataURL，避免 html2canvas 跨域污染画布 */
export function remoteImageToDataUrl(url) {
  if (!url || !isRemoteUrl(url)) {
    return Promise.resolve(url);
  }

  const normalizedUrl = url.indexOf('//') === 0 ? window.location.protocol + url : url;

  return loadCorsImageAsDataUrl(normalizedUrl)
    .catch(() => loadCorsImageAsDataUrl(appendCacheBuster(normalizedUrl)))
    .catch(() => fetchImageAsDataUrl(normalizedUrl))
    .catch(() => fetchImageAsDataUrl(appendCacheBuster(normalizedUrl)))
    .then((result) => {
      if (result && String(result).indexOf('data:') === 0) {
        return result;
      }
      return url;
    });
}

async function inlineImage(img, src) {
  if (!src || src.indexOf('data:') === 0) {
    return null;
  }

  let dataUrl = '';
  if (!isRemoteUrl(src) && img.complete && img.naturalWidth > 0) {
    try {
      dataUrl = drawImageToDataUrl(img);
    } catch (e) {
      dataUrl = '';
    }
  }

  if (!dataUrl || dataUrl.indexOf('data:') !== 0) {
    if (isRemoteUrl(src)) {
      dataUrl = await remoteImageToDataUrl(src);
    }
  }

  if (dataUrl && dataUrl.indexOf('data:') === 0) {
    return { img, src, dataUrl };
  }
  return null;
}

// 截图前将远程图内联为 dataURL，结束后 restoreImages 还原
async function prepareImagesForCapture(container) {
  const element = resolvePosterElement(container);
  if (!element) {
    return [];
  }

  const backups = [];
  const images = Array.from(element.querySelectorAll('img'));

  for (let i = 0; i < images.length; i++) {
    const img = images[i];
    const src = img.currentSrc || img.getAttribute('src');
    const result = await inlineImage(img, src);
    if (result) {
      backups.push({ img, src: result.src });
      img.removeAttribute('crossorigin');
      img.src = result.dataUrl;
      await waitOneImage(img, 5000);
    }
  }

  return backups;
}

function restoreImages(backups) {
  backups.forEach(({ img, src }) => {
    img.src = src;
  });
}

function canvasToPosterDataUrl(canvas) {
  try {
    return canvas.toDataURL('image/png');
  } catch (e) {
    return canvas.toDataURL('image/jpeg', 0.92);
  }
}

/**
 * 对海报 DOM 执行 html2canvas 截图，结果写入 posterState
 * @returns {boolean} 是否截图成功
 */
export async function capturePosterElement(targetDom, posterState) {
  if (posterState) {
    if (capturingPosterStates.has(posterState)) {
      return false;
    }
    capturingPosterStates.add(posterState);
  }

  const element = resolvePosterElement(targetDom);
  if (!element) {
    if (posterState) {
      capturingPosterStates.delete(posterState);
    }
    finishPosterCapture(posterState);
    showPosterToast('海报生成失败');
    return false;
  }

  let backups = [];
  try {
    await waitForPosterImages(element);
    backups = await prepareImagesForCapture(element);
    await waitForPosterImages(element);

    const width = element.scrollWidth || element.offsetWidth;
    const height = element.scrollHeight || element.offsetHeight;
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const canvas = await html2canvas(element, {
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: null,
      width,
      height: Math.max(height, 1),
      scale,
      imageTimeout: 15000,
    });

    posterState.posterUrl = canvasToPosterDataUrl(canvas);
    posterState.isShow = true;
    posterState.isPoster = -1;
    finishPosterCapture(posterState);
    showPosterToast('长按保存图片');
    return true;
  } catch (err) {
    console.warn('capturePoster error', err);
    finishPosterCapture(posterState);
    showPosterToast('海报图片加载失败，请重试');
    return false;
  } finally {
    restoreImages(backups);
    if (posterState) {
      capturingPosterStates.delete(posterState);
    }
  }
}
